import { ConfigServices, Services } from '../services';
import { action, computed, observable, reaction } from "mobx";
import { theme } from '../Components/theme';
import { mergeAppStylesWithTheme } from '@kurtosys/ksys-portal-components/build/utils/mergeAppStylesWithTheme';
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import { getParams, getFilterParams } from "../utils";
import getSortKeyFromColumn from "../utils/getSortKeyFromColumn";
import isExistingAdvancedSort from "../utils/isExistingAdvancedSort";
import * as FileSaver from "file-saver";
import { ConfigStore } from './ConfigStore';
import { EXPANDED_PARAM, EXPAND_ALL_PARAM, DISALLOWED_FILTER_PARAMS, SORT_DIRECTIONS, ADVANCED_SORT_TYPES } from '../constants';

interface Config {
    currentModuleId: string;
    currentFundType: string;
    fundTypeProperty: string;
    selectedFundType: FundType;
    stickyOffset: number;
    defaultCulture: string;
    watchListEnabled: boolean;
    watchListProperty: string;
    fundGroupProperty: string;
    fundCurrencyProperty: string;
    identifierSearch: IdentifierSearch;
    documentsJoinSpec: Array<DocumentJoinSpec>;
    documentSearch: Array<Search>;
    documentSearchBySegmentation: Array<Search>;
    filtersConfig: Array<Filter>;
    filtersTop: boolean;
    filtersTopExpanded: boolean;
    filtersTopExpandable: boolean;
    sort: Array<EntitySort>;
    advancedSort: AdvancedSort;
    limit: number;
    cultureAliases: { [property: string]: string };
    headerDisclaimerTypes: Array<string>;
    footerDisclaimerTypes: Array<string>;
    rowConfig: FundCardRowConfig | DocumentsRowConfig;
    documentGroupMeta: string;
    timeseriesInclude: IncludeData;
    statisticsInclude: IncludeData;
    allocationsInclude: IncludeData;
    fundTypes: Array<FundType>;
    setFundType: Function;
    modules: Array<Module>;
    setModule: Function;
    selectedModule: Module;
    columnConfig: Array<{}>;
}

interface IncludeData {
    limitTo?: Array<string>;
}

interface FundType {
    fundType: string;
    label: string;
    showModules: boolean;
    selected: boolean;
    modules: Array<Module>;
}

interface Module {
    id: string;
    label: string;
    selected: boolean;
    config: Config;
}

interface Filter {
    type: "CLSS" | "DOCUMENT_META" | "DOCUMENT_PROPERTY";
    property: string;
    label: string;
    ui: string;
    enableSearch: boolean;
    allowedValues?: Array<string>;
    valueAliases?: { [property: string]: string };
}

interface IdentifierSearch {
    properties: Array<string>;
    value: string;
    fuzzy: boolean;
}

interface DocumentJoinSpec {
    joinTo: string;
    joinFrom: string;
    search?: Array<Search>;
}

interface Search {
    property: string;
    values: Array<string>;
    matchtype: "MATCH" | "LIKE";
}

type SortDirections = "ASC" | "DESC";

interface EntitySort {
    key: string;
    direction: SortDirections;
}

interface AdvancedSort {
    type: "Statistics" | "Allocations";
    code: string;
    key: string;
    value: string;
    direction?: SortDirections;
}

interface Params {
    [property: string]: string | undefined;
}

interface FundCardRowConfig {
    type: "FundCard";
    fundHeaderProperty: string;
    fundPageProperty: string;
    fundFactCards: Array<any>; // config TBD
    fundFactRows: Array<any>; // config TBD
    fundManagers: Array<any>; // config TBD
    charts: Array<ChartConfig>;
}

interface DocumentsRowConfig {
    type: "Documents";
    // config TBD
}

interface DataConfig {
    property: "allocations" | "statistics" | "timeseries";
    code: string;
    ccy: string;
    classification?: string;
    periodicity?: string;
    key?: string;
    index?: number;
}

interface ChartDisclaimerType {
    disclaimerType: string;
    linked: boolean;
}

interface ChartConfig {
    id: string;
    label: string;
    selected: boolean;
    grouped: boolean;
    type: "LineChart" | "BarChart";
    data: DataConfig;
    // Chart config is recharts component options
    config: {
        legend: boolean | {};
        grid: boolean | {};
        tooltip: boolean | {};
        xAxis: {};
        yAxis: {};
        series: Array<{}>;
    };
    asAtDate: {
        label: string;
        data: DataConfig;
        format: {
            type: "currency" | "date" | "number"
        }
    };
    noDataDisclaimerTypes: Array<ChartDisclaimerType>;
    disclaimerTypes: Array<ChartDisclaimerType>;
}

export class DataStore {
    configServices;
    services;
    apiUrl;
    baseUrl;

    // App config
    @observable token = '';  // API token
    theme = {}; // App theme
    @observable config = <Config> {}; // App config from config file
    @observable currentModuleId = undefined; // Module id set by params or tab selection
    @observable currentFundType = undefined; // Fund type set by params or tab selection

    // App loading states
    @observable isBootstrapped = false;
    @observable isLoading = true;
    @observable isLoadingMore = false;
    @observable isExpanded = false;
    @observable isExpandAll = false;

    // Translation & culture
    @observable translationData = []; // Translation dictionary
    @observable defaultCulture = undefined; // this.config.defaultCulture; // Culture to use
    @observable cultures = []; // System cultures

    // Disclaimers
    @observable disclaimers = [];
    @observable disclaimersDictionary = {};

    // Search
    @observable fundlist = ''; // Segmentation list
    @observable identifierSearchValue = ''; // Search term
    @observable total = 0; // Search total
    @observable start = 0; // Search start
    @observable funds = []; // Search data

    // Filters
    @observable filters = {}; // Filter codes and values
    @observable activeFilters = {}; // Filter codes and values returned by getFilters with search
    @observable filterSearch = []; // Selected entity filters
    @observable filterParams = {}; // Filters from query string parameters
    @observable documentFilterSearch = []; // Selected document meta filters
    @observable documentPropertySearch = []; // Selected document property filters

    // Watchlist
    @observable watchList = [];
    @observable watchListActive = false;

    // Sorting
    @observable propertySort = [];
    @observable advancedSort;

    // Map of column index to selected column group index
    selectedGroupMap = observable.map();

    constructor(props) {
        // Call init function with props passed from the main app component
        // Props will be the attributes from the element where the app is mounted
        this.init(props);

        // Search called in reaction to changes in search term
        // This is debounced to allow the user to finish typing
        reaction(
            () => this.identifierSearchValue,
            (identifierSearchValue) => {
                this.getFunds();
            },
            { delay: 300 }
        );

        // Search and getActiveFilters in reaction to: 
        // entity filter selections
        // watchList toggle
        reaction(
            () => [
                this.filterSearch,
                this.watchListActive
            ],
            (filters) => {
                if (this.isBootstrapped) {
                    this.getFunds();
                    this.getActiveFilters();
                }
            }
        );

        // Save watchList to local storage
        reaction(
            () => this.watchList,
            (watchList) => {
                localStorage.setItem('watchList', JSON.stringify(watchList));
            }
        );

        // Reaction to change in module/tab gets funds and filters for new config
        reaction(
            () => this.config.currentModuleId,
            (currentModuleId) => {
                if (this.isBootstrapped) {
                    this.setInitialSort();
                    this.getFunds();
                    this.getFilters();
                    this.getActiveFilters();
                    this.getDocumentFilters();
                }
            }
        );

        // Reaction to change in fund type clears filters and gets filters for new config
        reaction(
            () => this.config.currentFundType,
            (currentFundType) => {
                if (this.isBootstrapped) {
                    this.clearFilters();
                    this.getFilters();
                    this.getActiveFilters();
                    this.getDocumentFilters();
                }
            }
        );
    }

    // Init function to boot the app
    @action
    init = async (props) => {
        this.getParams(props);
        this.getApiUrl(props.scriptSrc);
        this.getWatchList();

        this.configServices = new ConfigServices(this.apiUrl);

        await this.getApplicationAppConfig();

        this.services = new Services(this.apiUrl, this.token);

        await this.getConfigFile();

        this.setInitialSort();

        this.addFilterSearch();

        Promise.all([
            await this.getDisclaimers(),
            await this.getTranslations(),
            await this.getCultures(),
            await this.getFilters(),
            await this.getDocumentFilters(),
            await this.getCultureFilters()
        ]);
        
        this.isBootstrapped = true;
        
        Promise.all([
            await this.getFunds(),
            await this.getActiveFilters()
        ]);

        // If Intl not supported then add locale data
        if (!props.intlSupported) {
            await this.addLocaleData();
        }

        this.isLoading = false;
    }

    // Sets various values from props or query string parameters
    // Query string parameters take precedence over props
    @action
    getParams = (props) => {
        const {
            fundlist = props.fundlist,
            culture = props.culture,
            tab = props.tab,
            categoryTab = props.categorytab
        } = getParams(location.search) as Params;

        this.filterParams = getFilterParams(location.search);
        this.isExpanded = this.filterParams.hasOwnProperty(EXPANDED_PARAM);
        this.isExpandAll = this.filterParams.hasOwnProperty(EXPAND_ALL_PARAM);
        this.fundlist = fundlist || "";
        this.defaultCulture = culture;
        this.currentModuleId = tab;
        this.currentFundType = categoryTab;
    }

    // Parses an api url from the app script tag src... but why?!
    // Our services need to make api calls via the clients app instance url, which will be different for each client app.
    // We can't store it in the app config because we would need to know the app url to get it...
    // However, the script tag for the app will use the app url for the main javascript file.
    // E.g. https://api.fundpress.io/applicationManager/apps/xxxx-xxxx-xxxx-xxxx/main.js
    // So we pass the src of our own script tag into the app where we can get parse the app instance url.
    // We can then pass this to our services classes so api calls can hit the app url.
    @action
    getApiUrl = (scriptSrc) => {
        let apiUrl = scriptSrc.split('/').slice(0, 6).join('/');
        let baseUrl = scriptSrc.split('/').slice(0, 3).join('/');

        if (!apiUrl.includes('applicationManager/apps')) {
            apiUrl = '';
        }

        this.apiUrl = apiUrl;
        this.baseUrl = baseUrl;
    }

    // Gets application config for a token and theme
    @action
    getApplicationAppConfig = async () => {
        try {
            const config = await this.configServices.getApplicationAppConfig();
            this.theme = mergeAppStylesWithTheme(config.applicationStyles, theme);
            this.token = get(config, 'applicationConfiguration.apiToken');
        } catch (e) {
            console.log(e);
        }
    }

    // Gets the config file 
    @action
    getConfigFile = async () => {
        try {
            const configFile = await this.configServices.getConfigFile();
            this.config = new ConfigStore(configFile, this.currentModuleId, this.currentFundType, this.fundlist);
        } catch (e) {
            console.log(e);
        }
    }

    // Gets an Intl polyfill locale data file, e.g: fr-FR.json
    // then makes local data available to polyfilled Intl
    @action
    addLocaleData = async () => {
        try {
            const localeData = await this.configServices.getLocaleFile(this.culture);
            (window as any).Intl.__addLocaleData(localeData);
        } catch (e) {
            console.log(e);
        }
    }

    // Updates filter search and document filter search based on filter params from query string.
    // If a filter property matches config.documentGroupMeta (e.g. document_type) then we can add a document filter search.
    // Ideally, _prefixed query string parameters would be reserved for filters,
    // but _expanded was already specified to a client, so we now have to handle it.
    @action
    addFilterSearch = () => {
        Object.keys(this.filterParams).map(property => {
            if (!DISALLOWED_FILTER_PARAMS.includes(property)) {
                const values = this.filterParams[property] ? this.filterParams[property].split("|") : [""];

                if (property === this.config.documentGroupMeta) {
                    this.documentFilterSearch.push({
                        property: property,
                        values: values,
                        matchtype: "MATCH"
                    });
                } else {
                    this.filterSearch.push({
                        property: property,
                        values: values,
                        matchtype: "MATCH"
                    });
                }
            }
        });
    }

    // Add configured advancedSort
    // Reset property sort
    @action
    setInitialSort = () => {
        this.advancedSort = this.config.advancedSort;
        this.propertySort = [];
    }

    @computed
    get fundTypeSearch() {
        if (this.config.fundTypeProperty) {
            return [{
                property: this.config.fundTypeProperty,
                values: [this.config.selectedFundType.fundType],
                matchtype: "MATCH"
            }];
        }

        return [];
    }

    @computed
    get groupedFunds() {
        const groupedFunds = groupBy(this.funds, `properties_pub.${this.config.fundGroupProperty}.value`);
        return observable(Object.keys(groupedFunds).map(fundGroupName => (
            {
                fundName: fundGroupName,
                grouped: this.config.fundGroupProperty ? true : false,
                shareClasses: groupedFunds[fundGroupName]
            }
        )));
    }

    @computed
    get endOfResults() {
        return this.funds.length === this.total;
    }

    @computed
    get culture() {
        return this.defaultCulture || this.config.defaultCulture;
    }

    // Watchlist

    @computed
    get watchListEnabled() {
        return (this.config.watchListEnabled && this.config.watchListProperty) ? true : false;
    }

    @computed
    get watchListSearchEnabled() {
        return (this.config.watchListEnabled && this.watchListActive) ? true : false;
    }

    @computed
    get watchListCount() {
        return this.watchList.length;
    }

    @action
    getWatchList = () => {
        this.watchList = JSON.parse(localStorage.getItem('watchList')) || [];
    }

    @action
    toggleWatchList = () => {
        this.watchListActive = !this.watchListActive;
    }

    @computed
    get watchListSearch() {
        return [{
            property: this.config.watchListProperty,
            values: this.watchList,
            matchtype: "MATCH"
        }];
    }

    // Identifer search config used in entity searches
    // This is derived from merging config.identifierSearch with a search term.
    // We use the property used for grouping funds as the default property to search on. Note that this may be overriden by identifierSearch config.
    @computed
    get identifierSearch() {
        return {
            properties: [this.config.fundGroupProperty || ""],
            fuzzy: false,
            ...this.config.identifierSearch,
            ...{ value: this.identifierSearchValue }
        };
    }

    // Documents join spec used in entity searches
    // Returns config.documentsJoinSpec combined with documentSearch and documentSearchBySegmentation to limit the documents displayed.
    @computed
    get documentsJoinSpec() {
        const joinSpecSearch = this.config.documentSearch
            .concat(this.config.documentSearchBySegmentation.slice())
            .reduce((accumulator, currentValue) => {
                const existingPropertySearch = accumulator.find(a => a.property === currentValue.property);

                if (existingPropertySearch) {
                    existingPropertySearch.values = existingPropertySearch.values.concat(currentValue.values);
                    return accumulator;
                }

                accumulator.push(currentValue);

                return accumulator;
            }, []);

        return this.config.documentsJoinSpec.map(joinSpec => {
            joinSpec.search = joinSpecSearch;
            return joinSpec;
        });
    }

    // Get filter properties that apply to entity search from filters config and query string filter params
    @computed
    get filterProperties() {
        return this.config.filtersConfig
            .filter(filter => filter.type === "CLSS")
            .map(filter => filter.property)
            .concat(Object.keys(this.filterParams))
            .filter(property => !DISALLOWED_FILTER_PARAMS.includes(property));
    }

    // Build a search for fund/getFilters based on allowedValues specified in filter config
    @computed
    get filterPropertiesAllowedValuesSearch() {
        return this.config.filtersConfig
            .filter(filter => filter.type === "CLSS")
            .map(filter => {
                if (filter.allowedValues && filter.allowedValues.length > 0) {
                    return {
                        property: filter.property,
                        values: filter.allowedValues || [],
                        matchtype: "MATCH"
                    };
                }
            })
            .filter(filter => filter !== undefined);
    }

    // Get filter properties that apply to document meta from filters config
    @computed
    get documentFilterCodes() {
        return this.config.filtersConfig.filter(filter => filter.type === "DOCUMENT_META").map(filter => filter.property);
    }

    // Build a search for documents/getFilters based on allowedValues specified in filter config
    @computed
    get documentFilterAllowedValuesSearch() {
        return this.config.filtersConfig
            .filter(filter => filter.type === "DOCUMENT_META")
            .map(filter => {
                if (filter.allowedValues && filter.allowedValues.length > 0) {
                    return {
                        property: filter.property,
                        values: filter.allowedValues || [],
                        matchtype: "MATCH"
                    };
                }
            })
            .filter(filter => filter !== undefined);
    }

    // Get filters that apply to document properties from filters config
    @computed
    get documentPropertyFilters() {
        return this.config.filtersConfig.filter(filter => filter.type === "DOCUMENT_PROPERTY");
    }

    // Builds a translation dictionary object for the current culture from all translation data. E.g:
    // { 'phrase': 'translation', 'phrase': 'translation', etc }
    @computed
    get translations() {
        const translationDictionary = {};

        this.translationData
            .filter(translation => translation.translationCulture === this.culture)
            .map(translation => translationDictionary[translation.phrase] = translation.translation);

        return translationDictionary;
    }

    // Get sort matching fundGroupProperty
    @computed
    get fundGroupPropertySort() {
        return this.config.sort.find(s => s.key === this.config.fundGroupProperty);
    }

    // Toggle sort
    @action
    toggleSort = (column) => {
        const property = get(column, 'data.property', '');

        if (Object.keys(ADVANCED_SORT_TYPES).includes(property)) {
            this.toggleAdvancedSort(column);
        } else {
            this.togglePropertySort(column);
        }
    }

    // Toggle property sort
    @action
    togglePropertySort = (column) => {

        // Get sort key
        const key = getSortKeyFromColumn(column);

        // Find the selected column in propertySort
        const existingColumn = this.propertySort.find(s => s.key === key);

        // If there is an existing sort, toggle the direction
        // If there is not an existing sort, replace with new sort
        if (existingColumn) {
            existingColumn.direction = (existingColumn.direction === SORT_DIRECTIONS.ASC) ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC;

            this.propertySort = [
                existingColumn
            ];
        } else {
            this.propertySort = [{
                key: key,
                direction: SORT_DIRECTIONS.ASC
            }];
        }

        // If a sort is configured matching fundGroupProperty, add to top of sort array
        if (this.fundGroupPropertySort) {
            this.propertySort.unshift(this.fundGroupPropertySort);
        }

        // Clear advancedSort
        this.advancedSort = undefined;

        // Get data
        this.getFunds();
    }

    // Toggle advanced sort
    @action
    toggleAdvancedSort = (column) => {

        // Build advanced search object
        const { property, code, key, sortKey = '' } = column.data;

        const advancedSort = {
            type: ADVANCED_SORT_TYPES[property], 
            code, 
            key: sortKey, 
            value: key, 
            direction: SORT_DIRECTIONS.ASC
        };
        
        // See if we have already have matching advanced sort 
        const existingSort = isExistingAdvancedSort(column, this.advancedSort);

        // If existing advanced sort, toggle the direction
        // If there is not an existing advanced sort, set it
        if (existingSort) {
            this.advancedSort.direction = (this.advancedSort.direction === SORT_DIRECTIONS.ASC) ? SORT_DIRECTIONS.DESC : SORT_DIRECTIONS.ASC;
        } else {
            this.advancedSort = advancedSort;
        }

        // Reset property sort
        this.propertySort = [];

        // Get data
        this.getFunds();
    }

    // Get translations
    @action
    getTranslations = async () => {
        try {
            this.translationData = await this.services.getTranslation(this.config.defaultCulture);
        } catch (e) {
            console.log(e);
        }
    }

    // Get fund data
    @action
    getFunds = async (paged = false) => {
        // Set start page
        this.start = paged ? this.start + this.config.limit : 0;

        // If loading initial results show main loading
        this.isLoading = !paged;

        // If loading next page of results show loading more
        this.isLoadingMore = paged;

        // Remove filter searches not currently in filter properties
        // This ensures that only configured filters and query param filters are applied, 
        // whilst retaining filter searches when switching between tabs 
        const filterSearch = this.filterSearch.filter(s => this.filterProperties.includes(s.property));

        const search = this.watchListSearchEnabled ? filterSearch.concat(this.watchListSearch) : filterSearch.concat(this.fundTypeSearch);

        try {
            const { total, values } = await this.services.getData(
                this.fundlist,
                search,
                this.identifierSearch,
                this.propertySort.length > 0 ? this.propertySort : this.config.sort,
                this.advancedSort,
                this.documentsJoinSpec,
                this.start,
                this.config.limit,
                this.culture,
                this.config.defaultCulture,
                this.config.timeseriesInclude,
                this.config.statisticsInclude,
                this.config.allocationsInclude
            );

            // If paged then join to existing data, otherwise set new data
            this.funds = paged ? this.funds.concat(values) : values;
            this.total = total;
        } catch (e) {
            console.log(e);
        }

        this.isLoading = false;
        this.isLoadingMore = false;
    }

    // Set search term
    @action
    search = (value) => {
        this.identifierSearchValue = value;
    }

    // Clear search term
    @action
    clearSearch = () => {
        this.identifierSearchValue = '';
    }

    // Clear filters
    @action
    clearFilters = () => {
        this.filterSearch = [];
        this.documentFilterSearch = [];
        this.documentPropertySearch = [];
    }

    // Get values for specified properties limited by any allowed values specified in filter config
    @action
    getFilters = async () => {
        if (this.filterProperties.length > 0) {
            const search = this.filterPropertiesAllowedValuesSearch.concat(this.fundTypeSearch);

            try {
                const filters = await this.services.getFilters(this.filterProperties, search, this.fundlist);
                Object.keys(filters).forEach(filter => {
                    const { valueAliases = {} } = this.config.filtersConfig.find(f => f.property === filter) || {};
                    filters[filter] = filters[filter].map(f => ({
                        label: get(valueAliases, f.label, f.label),
                        value: f.value
                    }));
                });
                this.filters = { ...this.filters, ...filters };
            } catch (e) {
                console.log(e);
            }
        }
    }

    // Get values for specified properties limited by filter property selections
    @action
    getActiveFilters = async () => {
        if (this.filterProperties.length > 0) {
            const search = this.watchListSearchEnabled ? this.filterSearch.concat(this.watchListSearch) : this.filterSearch;

            try {
                this.activeFilters = await this.services.getFilters(this.filterProperties, search, this.fundlist);
            } catch (e) {
                console.log(e);
            }
        }
    }

    // Get values for specified document meta, limited by any allowed values specified in filter config
    // Label is replaced by value for consistency with fund/getFilters
    @action
    getDocumentFilters = async () => {
        if (this.documentFilterCodes.length > 0) {
            try {
                const filters = await this.services.getDocumentFilters(this.documentFilterCodes, this.documentFilterAllowedValuesSearch);
                Object.keys(filters).forEach(filter => {
                    const { valueAliases = {} } = this.config.filtersConfig.find(f => f.property === filter) || {};
                    filters[filter] = filters[filter].map(f => ({
                        label: get(valueAliases, f.label, f.label),
                        value: f.value
                    }));
                });
                this.filters = { ...this.filters, ...filters };
            } catch (e) {
                console.log(e);
            }
        }
    }

    // Get system cultures
    @action
    getCultures = async () => {
        try {
            this.cultures = await this.services.getCultures();
        } catch (e) {
            console.log(e);
        }
    }

    // Add cultureCode to filters in the same format as returned by getFilters for entities and documents.
    // cultureCode: [{ label: 'English', value: 'en-GB' }]
    // This enables us to handle selections in the same way as other filters.
    // Also alias labels as defined in config.cultureAliases (takes precedence) or filter.valueAliases
    @action
    getCultureFilters = async () => {
        const { allowedValues = [], valueAliases = {} } = this.documentPropertyFilters.find(filter => filter.property === 'cultureCode') || {};
        const cultureFilters = {
            cultureCode: this.cultures
                .filter(culture => allowedValues.length > 0 ? allowedValues.includes(culture.culture) : true)
                .map(culture => ({
                    label: get(this.config.cultureAliases, culture.culture) || get(valueAliases, culture.culture, culture.culture),
                    value: culture.culture
                }))
        };
        this.filters = { ...this.filters, ...cultureFilters };
    }

    // Download documents zip with document clientCodes
    @action
    downloadDocuments = async (clientCodes: Array<string> = []) => {
        if (clientCodes.length > 0) {
            this.isLoading = true;

            const downloadZip = clientCodes.length > 1 ? true : false;
            const filename = downloadZip ? 'documents.zip' : clientCodes[0];

            try {
                const documentData = downloadZip
                    ? await this.services.documentDownloadZip(clientCodes)
                    : await this.services.documentDownload(clientCodes);

                FileSaver.saveAs(
                    documentData,
                    filename
                );
            } catch (e) {
                console.log(e);
            }

            this.isLoading = false;
        }
    }

    // Download CSV file from CSV string
    @action
    downloadCsv = async (csv: string, filename: string) => {
        this.isLoading = true;

        try {
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });

            FileSaver.saveAs(
                blob,
                filename
            );
        } catch (e) {
            console.log(e);
        }

        this.isLoading = false;
    }

    // Get disclaimers
    @action
    getDisclaimers = async () => {
        try {
            this.disclaimers = await this.services.getDisclaimers();

            const disclaimers = groupBy(this.disclaimers, (d) => {
                const { linkedItem = '*' } = d;
                return linkedItem;
            });

            for (let d in disclaimers) {
                if (d) {
                    disclaimers[d] = groupBy(disclaimers[d], 'disclaimerType');

                    for (let dt in disclaimers[d]) {
                        if (dt) {
                            const disclaimer =
                                disclaimers[d][dt][0].disclaimers.find(disclaimerContent => disclaimerContent.culture === this.culture) ||
                                disclaimers[d][dt][0].disclaimers.find(disclaimerContent => disclaimerContent.culture === this.config.defaultCulture);

                            disclaimers[d][dt] = get(disclaimer, 'disclaimer', '');
                        }
                    }
                }
            }

            this.disclaimersDictionary = disclaimers;
        } catch (e) {
            console.log(e);
        }
    }

    // Get disclaimers for header and footer as specified in config 
    // Since the intended use is to display general purpose disclaimers not specifically for a fund,
    // we assume that disclaimers for the given types are not linked to an entity or segmentation list and do not have multiple disclaimers.
    @computed
    get headerDisclaimers() {
        return this.getUnlinkedDisclaimers(this.config.headerDisclaimerTypes);
    }

    @computed
    get footerDisclaimers() {
        return this.getUnlinkedDisclaimers(this.config.footerDisclaimerTypes);
    }

    // Sets the selected group of dropdown columns
    @action
    setSelectedGroup = (columnIndex, groupIndex) => {
        this.selectedGroupMap.set(columnIndex, groupIndex);
    }

    @computed
    get columns() {
        const processedColumns = this.config.columnConfig.map((column, columnIndex) => {
            const { type, groups = [], columns = [], disclaimerTypes = [] } = column as any;

            if (type === 'dropdown' && groups.length > 0) {
                return (
                    {
                        type: 'dropdown',
                        groups: groups.map(g => (
                            {
                                ...g,
                                type: 'dropdown',
                                columnIndex: columnIndex,
                                options: groups.map((c, i) => ({ groupIndex: i, label: c.label })),
                                columns: (g.columns && g.columns.length > 0)
                                    ? g.columns.map(c => ({
                                        ...c,
                                        disclaimers: this.getUnlinkedDisclaimers(c.disclaimerTypes)
                                    }))
                                    : [{
                                        ...g,
                                        disclaimers: this.getUnlinkedDisclaimers(g.disclaimerTypes)
                                    }],
                                disclaimers: this.getUnlinkedDisclaimers(g.disclaimerTypes)
                            }
                        ))
                    }
                );
            }

            if (type === 'group' && columns.length > 0) {
                return (
                    {
                        ...column,
                        columns: columns.map(c => ({
                            ...c,
                            disclaimers: this.getUnlinkedDisclaimers(c.disclaimerTypes)
                        })),
                        disclaimers: this.getUnlinkedDisclaimers(disclaimerTypes)
                    }
                );
            }

            return (
                {
                    ...column,
                    disclaimers: this.getUnlinkedDisclaimers(disclaimerTypes)
                }
            );
        });

        return processedColumns;
    }

    @computed
    get sortableColumns() {
        const sortableColumns = [];
        this.config.columnConfig.map(c => {
            const { label, sortable, columns, groups, data } = c as any;

            if (sortable) {
                sortableColumns.push(
                    {
                        ...c
                    }
                );
            }

            if (data && data.length > 0) {
                data.map(cc => {
                    if (cc.sortable) {
                        sortableColumns.push(
                            {
                                ...cc,
                                property: cc.code,
                                label: `${label}`
                            }
                        );
                    }
                });
            }

            if (columns) {
                columns.map(cc => {
                    if (cc.sortable) {
                        sortableColumns.push(
                            {
                                ...cc,
                                label: `${label}: ${cc.label}`
                            }
                        );
                    }
                });
            }

            if (groups) {
                groups.map(g => {
                    if (g.columns) {
                        g.columns.map(gc => {
                            if (gc.sortable) {
                                sortableColumns.push(
                                    {
                                        ...gc,
                                        label: `${g.label}: ${gc.label}`
                                    }
                                );
                            }
                        });
                    } else {
                        sortableColumns.push(
                            {
                                ...g,
                                label: `${g.label}`
                            }
                        );
                    }
                });
            }
        });

        return sortableColumns;
    }

    getUnlinkedDisclaimers = (disclaimerTypes = []) => {
        const disclaimers = disclaimerTypes
            .map(d => get(this.disclaimersDictionary, `*.${d}`))
            .filter(d => d !== undefined);

        return disclaimers;
    }
}


// WEBPACK FOOTER //
// ./src/stores/DataStore.ts